import React from 'react';
import { Scales as Scale, UploadSimple as Upload, Trash as Trash2 } from "@phosphor-icons/react";

const STATUS_META = {
  'on-track': { label: 'On Track', color: '#4CAF50' },
  solid: { label: 'Solid', color: '#4CAF50' },
  nice: { label: 'Nice', color: '#4CAF50' },
  great: { label: 'Great', color: '#4CAF50' },
  perfect: { label: 'Perfect', color: '#4CAF50' },
  tight: { label: 'Tight', color: '#E08A00' },
  'not-realistic': { label: 'Risky', color: '#DE3C4B' },
  unknown: { label: 'Sanity Check', color: '#2E86AB' }
};

const buildRows = (a, b, fmt) => {
  const basis = (s) => s.formData.hasTargetInMind && s.formData.monthlyTarget
    ? `Target: ${fmt(parseFloat(s.formData.monthlyTarget))}/mo`
    : `Expenses: ${fmt(parseFloat(s.formData.monthlyExpenses) || 0)}/mo`;
  const debt = (s) => s.formData.debtAmount && parseFloat(s.formData.debtAmount) > 0
    ? `${fmt(parseFloat(s.formData.debtAmount))} in ${parseInt(s.formData.debtMonths) || 12} mo`
    : ', ';
  const savings = (s) => s.formData.savingsGoal && parseFloat(s.formData.savingsGoal) > 0
    ? `${fmt(parseFloat(s.formData.savingsGoal))} in ${parseInt(s.formData.savingsMonths) || 12} mo`
    : ', ';
  const schedule = (s) => `${s.formData.daysPerWeek} days/wk • ${s.workingWeeks} weeks (${s.weeksOff} off)`;
  const buffer = (s) => s.formData.hasTargetInMind ? ', ' : `${s.formData.slowWeekBuffer}%`;
  const tax = (s) => `14.1% + ${s.formData.federalTaxRate}% federal`;

  return [
    { label: 'Starting point', a: basis(a), b: basis(b) },
    { label: 'Weekly gross target', a: a.weeklyGross, b: b.weeklyGross, money: true, big: true },
    { label: 'Daily target', a: a.dailyTarget, b: b.dailyTarget, money: true },
    { label: 'Weekly take-home', a: a.netWeekly, b: b.netWeekly, money: true },
    { label: 'Feasibility', a: a.feasibilityStatus, b: b.feasibilityStatus, status: true },
    { label: 'Debt payoff', a: debt(a), b: debt(b) },
    { label: 'Savings goal', a: savings(a), b: savings(b) },
    { label: 'Time-off set-aside', a: a.ptoWeekly > 0 ? `${fmt(a.ptoWeekly)}/wk` : ', ', b: b.ptoWeekly > 0 ? `${fmt(b.ptoWeekly)}/wk` : ', ' },
    { label: 'Schedule', a: schedule(a), b: schedule(b) },
    { label: 'Slow-week buffer', a: buffer(a), b: buffer(b) },
    { label: 'Taxes', a: tax(a), b: tax(b) }
  ];
};

const StatusChip = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.unknown;
  return (
    <span className="inline-flex items-center gap-1.5 font-body text-sm font-medium" style={{ color: meta.color }}>
      <span className="w-2 h-2 rounded-full" style={{ background: meta.color }} />
      {meta.label}
    </span>
  );
};

const SlotHeader = ({ slot, scenario, onLoad, onClear }) => (
  <div className="text-center">
    <p className="font-heading text-base font-bold mb-2" style={{ color: '#2A1E5C' }}>Scenario {slot}</p>
    <div className="flex justify-center gap-2">
      <button
        onClick={() => onLoad(slot)}
        className="flex items-center gap-1 px-3 py-1.5 rounded-full font-body text-xs font-medium transition-all hover:scale-105"
        style={{ background: '#2A1E5C', color: '#FFFFFF' }}
        data-testid={`scenario-${slot.toLowerCase()}-load-btn`}
      >
        <Upload className="w-3 h-3" />
        Load
      </button>
      <button
        onClick={() => onClear(slot)}
        className="flex items-center gap-1 px-3 py-1.5 rounded-full font-body text-xs transition-all hover:scale-105"
        style={{ background: 'rgba(222, 60, 75, 0.1)', color: '#DE3C4B' }}
        data-testid={`scenario-${slot.toLowerCase()}-clear-btn`}
      >
        <Trash2 className="w-3 h-3" />
        Clear
      </button>
    </div>
  </div>
);

export const ScenarioCompare = ({ scenarios, onLoad, onClear, formatCurrency }) => {
  const { A, B } = scenarios;
  if (!A && !B) return null;

  // Only one saved: compact strip
  if (!A || !B) {
    const slot = A ? 'A' : 'B';
    const s = A || B;
    return (
      <div className="max-w-2xl mx-auto mt-10 p-5 rounded-2xl" style={{ background: '#FFFFFF', border: '2px solid rgba(46, 134, 171, 0.3)' }} data-testid="scenario-strip">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(46, 134, 171, 0.12)' }}>
              <Scale className="w-5 h-5" style={{ color: '#2E86AB' }} />
            </div>
            <div>
              <p className="font-heading text-sm font-bold" style={{ color: '#2A1E5C' }}>
                Scenario {slot} saved, {formatCurrency(s.weeklyGross)}/week
              </p>
              <p className="font-body text-xs" style={{ color: '#8B7E6A' }}>
                Save one more scenario to compare them side by side.
              </p>
            </div>
          </div>
          <SlotHeader slot={slot} scenario={s} onLoad={onLoad} onClear={onClear} />
        </div>
      </div>
    );
  }

  // Both saved: full comparison table
  const rows = buildRows(A, B, formatCurrency);

  return (
    <div className="max-w-3xl mx-auto mt-12" data-testid="scenario-compare-section">
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-3" style={{ background: 'rgba(46, 134, 171, 0.12)' }}>
          <Scale className="w-4 h-4" style={{ color: '#2E86AB' }} />
          <span className="font-body text-sm" style={{ color: '#2E86AB' }}>Scenario Comparison</span>
        </div>
        <h3 className="font-heading text-2xl font-medium" style={{ color: '#2A1E5C' }}>
          Which plan fits your year?
        </h3>
      </div>

      <div className="rounded-2xl overflow-hidden" style={{ background: '#FFFFFF', border: '2px solid rgba(42, 30, 92, 0.1)' }} data-testid="scenario-compare-table">
        {/* Header row */}
        <div className="grid grid-cols-[1fr_1fr_1fr] sm:grid-cols-[1.1fr_1fr_1fr] gap-2 p-4 items-end" style={{ background: 'rgba(42, 30, 92, 0.04)', borderBottom: '2px solid rgba(42, 30, 92, 0.08)' }}>
          <div />
          <SlotHeader slot="A" scenario={A} onLoad={onLoad} onClear={onClear} />
          <SlotHeader slot="B" scenario={B} onLoad={onLoad} onClear={onClear} />
        </div>

        {rows.map((row, idx) => {
          const differs = row.money
            ? Math.abs(row.a - row.b) > 0.005
            : row.a !== row.b;
          const delta = row.money ? row.b - row.a : 0;
          const cellStyle = differs ? { background: 'rgba(245, 203, 92, 0.12)' } : {};

          return (
            <div
              key={row.label}
              className="grid grid-cols-[1fr_1fr_1fr] sm:grid-cols-[1.1fr_1fr_1fr] gap-2 px-4 py-3 items-center"
              style={{ borderBottom: idx < rows.length - 1 ? '1px solid rgba(42, 30, 92, 0.06)' : 'none' }}
            >
              <p className="font-body text-xs sm:text-sm" style={{ color: '#8B7E6A' }}>{row.label}</p>

              {['a', 'b'].map((side) => {
                const value = row[side];
                return (
                  <div key={side} className="text-center rounded-lg py-1.5 px-1" style={cellStyle}>
                    {row.status ? (
                      <StatusChip status={value} />
                    ) : row.money ? (
                      <>
                        <p
                          className={`font-heading font-medium ${row.big ? 'text-lg sm:text-2xl' : 'text-sm sm:text-base'}`}
                          style={{ color: row.big ? '#2A1E5C' : '#5D5A6B' }}
                        >
                          {formatCurrency(value)}
                        </p>
                        {side === 'b' && differs && (
                          <p className="font-body text-xs font-medium" style={{ color: delta > 0 ? '#DE3C4B' : '#4CAF50' }}>
                            {delta > 0 ? '+' : '−'}{formatCurrency(Math.abs(delta))} vs A
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="font-body text-xs sm:text-sm" style={{ color: '#2A1E5C' }}>{value}</p>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      <p className="font-body text-xs text-center mt-3" style={{ color: '#8B7E6A' }}>
        Highlighted cells show where your scenarios differ. Load either one to keep tweaking it.
      </p>
    </div>
  );
};

export default ScenarioCompare;
